import { memo } from "react";
import { Check } from "lucide-react";
import { Label } from "@/shared/components/ui/label";
import { cn } from "@/shared/lib/utils";
import { PickupStockScanner } from "./PickupStockScanner";
import type { RentalStockItem } from "../../types/rental.types";

interface PickupStockChecklistProps {
  items: RentalStockItem[];
  selectedStockIds: number[];
  onToggle: (stockId: number) => void;
  onScanResolved: (stockId: number) => void;
}

/**
 * Checklist das peças vinculadas ao aluguel — todas devem ser marcadas
 * antes de confirmar a retirada (regra enforçada no dialog pai).
 *
 * Aceita dois fluxos: bipar via `PickupStockScanner` (auto-foco) ou
 * clicar diretamente nos cards das peças. Não faz fetch — `RentalStockItem`
 * já vem enriquecido pela API com barcode/produto/foto.
 */
export const PickupStockChecklist = memo(function PickupStockChecklist({
  items,
  selectedStockIds,
  onToggle,
  onScanResolved,
}: PickupStockChecklistProps) {
  const allSelected =
    items.length > 0 && selectedStockIds.length === items.length;

  return (
    <div className="space-y-3">
      <PickupStockScanner items={items} onMatch={onScanResolved} />

      <div className="flex items-center justify-between">
        <Label>
          Peças do aluguel ({selectedStockIds.length} de {items.length})
        </Label>
        {allSelected ? (
          <span className="inline-flex items-center gap-1 text-xs text-primary">
            <Check className="w-3.5 h-3.5" /> Todas marcadas
          </span>
        ) : null}
      </div>

      <ul className="space-y-2">
        {items.map((item) => {
          const checked = selectedStockIds.includes(item.id);
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onToggle(item.id)}
                className={cn(
                  "w-full flex items-center gap-3 rounded-md border p-3 text-left text-sm transition-colors",
                  checked
                    ? "border-primary bg-primary/5"
                    : "border-input bg-background hover:bg-accent/50",
                )}
                aria-pressed={checked}
              >
                <span
                  className={cn(
                    "w-5 h-5 rounded-sm border flex items-center justify-center shrink-0",
                    checked
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-input",
                  )}
                  aria-hidden="true"
                >
                  {checked ? <Check className="w-3.5 h-3.5" /> : null}
                </span>
                {item.photoUrl ? (
                  <img
                    src={item.photoUrl}
                    alt=""
                    className="w-10 h-10 rounded object-cover"
                  />
                ) : null}
                <span className="flex-1 truncate">
                  {item.barcode} · {item.productBrand} {item.productColor} ·{" "}
                  {item.productName} · Tam. {item.size}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
});
